import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useSelector } from "react-redux";
import { NisParticipantResponse } from "../../../../api/services/nisParticipants/models";
import {
  FormController,
  MuiGrid,
  MuiSelect,
  MuiTextField,
  OptionsDataType,
} from "../../../../components";
import {
  CountryField,
  CountryFieldDetails,
  TextFieldWithValidation,
} from "../../../../components/textFieldWithValidation/TextFieldWithValidation";
import {
  MAX_LENGTH_50,
  MAX_LENGTH_75,
  PAGE_IDS,
} from "../../../../helpers/constants";
import { getFieldRuleByCountry } from "../../../../helpers/constants/countryFieldRulesUtils";
import { commonSiteCoreKeys } from "../../../../helpers/constants/siteCoreKeys";
import {
  getVatPostalDetailsFromCountryKey,
  isEmpty,
  isNotEmpty,
} from "../../../../helpers/utils";
import { useGridContainer } from "../../../../hooks/useGridContainer";
import { useNavigationSiteCore } from "../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import { siteCoreContentLoadingSelector } from "../../../../redux/slices/appState/appStateSelector";
import { CreateNISParticipantFormKey } from "../../modelsAndSchemas/Models";
import { createNISParticipantAddressDetailsSiteCoreKeys } from "../../siteCore/SiteCoreKeys";
import { CreateAddressDetailsWrapper } from "./CreateAddressDetails.styles";

const pageId = PAGE_IDS.CREATE_NIS_PARTICIPANT_ADDRESS_DETAILS;

interface CreateAddressDetailsFormProps {
  countryVatPostalData?: CountryField[];
  isFormReset?: boolean;
  isEdit?: boolean;
  countryList: OptionsDataType[];
  editNISParticipantData?: NisParticipantResponse;
}

type PostalCodeRefHandle = {
  setDefaultValue: (defaultValue?: string) => void;
};

export const CreateAddressDetails: FC<CreateAddressDetailsFormProps> = ({
  countryVatPostalData,
  isFormReset,
  isEdit: isEditNISParticipant,
  countryList,
  editNISParticipantData,
}) => {
  const siteCoreLoading = useSelector(siteCoreContentLoadingSelector);
  const { gridSizeMedium: gridSize, gridSizeMediumClass } = useGridContainer();
  const {
    useFetchLabel,
    commonLabels,
    fetchValidationMessage,
    fetchDropdownValuesByPrefix,
  } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();

  const { control, getValues, setValue, resetField } = useFormContext();

  const [isDefaultPostalCodeSet, setIsDefaultPostalCodeSet] = useState(false);
  const postalCodeRef = useRef<PostalCodeRefHandle>(null);

  const watchPostalCode = useWatch({
    control,
    name: CreateNISParticipantFormKey.postalCode,
  });

  const professionalDetailsCountryWatch = useWatch({
    control,
    name: CreateNISParticipantFormKey.country,
  });

  const countryAddressWatch = useWatch({
    control,
    name: CreateNISParticipantFormKey.countryAddress,
  });

  useEffect(() => {
    if (
      (!isEditNISParticipant ||
        isEmpty(editNISParticipantData?.address?.country?.countryId)) &&
      isNotEmpty(professionalDetailsCountryWatch)
    ) {
      setValue(
        CreateNISParticipantFormKey.countryAddress,
        professionalDetailsCountryWatch,
      );
    }
  }, [
    professionalDetailsCountryWatch,
    isEditNISParticipant,
    editNISParticipantData,
  ]);

  const {
    addressDetailsTitle,
    address1,
    address2,
    address3,
    country: countryAddressLabel,
    city,
    stateProvince,
    postalCode,
    stateRegionProvinceListPrefix,
  } = createNISParticipantAddressDetailsSiteCoreKeys;

  const getLabel = useFetchLabel(addressDetailsTitle);

  useEffect(() => {
    const { postalCode } = getValues();
    if (isEditNISParticipant && !isDefaultPostalCodeSet && postalCode) {
      postalCodeRef.current?.setDefaultValue(postalCode ?? "");
      setIsDefaultPostalCodeSet(true);
    }
  }, [isEditNISParticipant, watchPostalCode]);

  const defaultCountryData: CountryFieldDetails = {
    label: getLabel(postalCode),
    placeHolder: commonLabels.inputPlaceHolder,
    maxLength: 10,
    regex: ".*",
    regexNotAllowed: "",
    formatErrorMessage: "",
    blurErrorMessage: "",
  };

  useEffect(() => {
    setValue(CreateNISParticipantFormKey.stateProvince, "");
  }, [countryAddressWatch]);

  const stateProvinceRule = getFieldRuleByCountry(
    countryAddressWatch,
    CreateNISParticipantFormKey.stateProvince,
  );

  const countrySpecificOptions = useMemo(() => {
    const stateOptions = fetchDropdownValuesByPrefix(
      addressDetailsTitle,
      stateRegionProvinceListPrefix,
      countryAddressWatch,
    );

    return { stateOptions };
  }, [countryAddressWatch, siteCoreLoading]);

  return (
    <CreateAddressDetailsWrapper>
      <form>
        <MuiGrid container className="main-container">
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <FormController
              control={control}
              name={CreateNISParticipantFormKey.address1}
            >
              <MuiTextField
                id={`${pageId}-${CreateNISParticipantFormKey.address1}`}
                label={getLabel(address1)}
                placeholder={commonLabels.inputPlaceHolder}
                maxLength={MAX_LENGTH_50}
                requiredARIALabel={commonARIALabel.required}
              />
            </FormController>
          </MuiGrid>

          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <FormController
              control={control}
              name={CreateNISParticipantFormKey.address2}
            >
              <MuiTextField
                id={`${pageId}-${CreateNISParticipantFormKey.address2}`}
                label={getLabel(address2)}
                placeholder={commonLabels.inputPlaceHolder}
                maxLength={MAX_LENGTH_50}
                requiredARIALabel={commonARIALabel.required}
              />
            </FormController>
          </MuiGrid>

          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <FormController
              control={control}
              name={CreateNISParticipantFormKey.address3}
            >
              <MuiTextField
                id={`${pageId}-${CreateNISParticipantFormKey.address3}`}
                label={getLabel(address3)}
                placeholder={commonLabels.inputPlaceHolder}
                maxLength={MAX_LENGTH_50}
                requiredARIALabel={commonARIALabel.required}
              />
            </FormController>
          </MuiGrid>

          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <FormController
              control={control}
              name={CreateNISParticipantFormKey.countryAddress}
            >
              <MuiSelect
                id={`${pageId}-${CreateNISParticipantFormKey.countryAddress}`}
                label={getLabel(countryAddressLabel)}
                visibleDefaultSelectOption={true}
                placeholder={commonLabels.selectPlaceHolder}
                options={countryList}
                requiredARIALabel={commonARIALabel.required}
                showSearch
                searchPlaceholder={commonLabels.search}
              />
            </FormController>
          </MuiGrid>

          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <FormController
              control={control}
              name={CreateNISParticipantFormKey.city}
            >
              <MuiTextField
                id={`${pageId}-${CreateNISParticipantFormKey.city}`}
                label={getLabel(city)}
                placeholder={commonLabels.inputPlaceHolder}
                maxLength={MAX_LENGTH_50}
                requiredARIALabel={commonARIALabel.required}
              />
            </FormController>
          </MuiGrid>

          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <FormController
              control={control}
              name={CreateNISParticipantFormKey.stateProvince}
            >
              {stateProvinceRule.mandatory ||
              stateProvinceRule.showDropdownForOptionalCountry ? (
                <MuiSelect
                  label={getLabel(stateProvince)}
                  options={countrySpecificOptions.stateOptions}
                  visibleDefaultSelectOption
                  value=""
                  id={`${pageId}-${CreateNISParticipantFormKey.stateProvince}`}
                  placeholder={commonLabels.selectPlaceHolder}
                  requiredARIALabel={commonARIALabel.required}
                  isMandatory={stateProvinceRule.mandatory}
                />
              ) : (
                <MuiTextField
                  id={`${pageId}-${CreateNISParticipantFormKey.stateProvince}`}
                  label={getLabel(stateProvince)}
                  placeholder={commonLabels.stateProvincePlaceHolder}
                  maxLength={MAX_LENGTH_75}
                  requiredARIALabel={commonARIALabel.required}
                />
              )}
            </FormController>
          </MuiGrid>

          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <FormController
              control={control}
              name={CreateNISParticipantFormKey.postalCode}
              ref={postalCodeRef}
            >
              <TextFieldWithValidation
                id={`${pageId}-${CreateNISParticipantFormKey.postalCode}`}
                formId={CreateNISParticipantFormKey.postalCode}
                isCountryChanged={countryAddressWatch}
                zeroValidationMessage={fetchValidationMessage(
                  commonSiteCoreKeys.greaterThanZeroFieldValidator,
                )}
                requiredFieldMessage={fetchValidationMessage(
                  commonSiteCoreKeys.requiredValidation,
                )}
                isFormReset={isFormReset}
                fieldDetails={{
                  ...(getVatPostalDetailsFromCountryKey(
                    countryAddressWatch,
                    countryVatPostalData,
                  )?.postalCode || defaultCountryData),
                }}
                requiredARIALabel={commonARIALabel.required}
              />
            </FormController>
          </MuiGrid>
        </MuiGrid>
      </form>
    </CreateAddressDetailsWrapper>
  );
};
